import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Breakpoint, ButtonBase, Stack, Typography, darken } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';

interface Props {
  text: string;
  stepCount: 1 | 2 | 3;
  breakpoint: Breakpoint;

  action?(): void;
}

const PaiStepFinished: React.FC<Props> = ({ text, stepCount, breakpoint, action }) => {
  const { classes, cx } = useStyles({ breakpoint });
  const { t } = useTranslation();

  const getStepCountTitle = () => {
    switch (stepCount) {
      case 1:
        return t('firstStep');

      case 2:
        return t('secondStep');

      case 3:
        return t('thirdStep');

      default:
        return '';
    }
  };

  return (
    <ButtonBase
      className={cx(classes.step, !_.isNil(action) ? 'clickable' : undefined)}
      onClick={!_.isNil(action) ? action : undefined}
      disableRipple={true}
    >
      <CheckCircleIcon className={classes.icon} />
      <div className={classes.stepFinished}>
        <Stack gap={2} alignItems={'center'} justifyContent={'flex-start'}>
          <Typography className={classes.text}>{`${getStepCountTitle()}: ${text}`}</Typography>
        </Stack>
      </div>
    </ButtonBase>
  );
};

const useStyles = makeStyles<{ breakpoint: Breakpoint }, 'stepFinished'>()((theme, { breakpoint }, classes) => ({
  step: {
    position: 'relative',
    background: '#FFF',
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
    padding: '83px 8px 16px 8px',
    height: '100%',
    width: '75px',
    '&.clickable': {
      cursor: 'pointer',
      '&:hover': {
        background: darken('#FFF', 0.05),
        [`& .${classes.stepFinished}`]: {
          border: `2px solid ${darken('#FFF', 0.05)}`,
        },
      },
    },
    [theme.breakpoints.down(breakpoint)]: {
      width: '100%',
      height: '75px',
      padding: '8px 16px 8px 83px',
    },
  },
  icon: {
    position: 'absolute',
    top: 'calc((83px - 40px) / 2)',
    left: 'calc(50% - 40px / 2)',
    fontSize: theme.typography.pxToRem(40),
    color: theme.palette.success.main,
    [theme.breakpoints.down(breakpoint)]: {
      left: 'calc((83px - 40px) / 2)',
      top: 'calc(50% - 40px / 2)',
    },
  },
  stepFinished: {
    height: '100%',
    width: '100%',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    paddingTop: '16px',
    [theme.breakpoints.down(breakpoint)]: {
      paddingLeft: '16px',
    },
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    writingMode: 'vertical-lr',
    textOrientation: 'sideways',
    transform: 'rotate(180deg)',
    [theme.breakpoints.down(breakpoint)]: {
      writingMode: 'unset',
      textOrientation: 'unset',
      transform: 'unset',
    },
  },
}));

export default PaiStepFinished;
